import { useEffect, useState } from "react";
import { formatTelepon } from "../lib/phone";
import { toast } from "sonner";
import { Plus, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { pesanGalatPembeli } from "../lib/galatPembeli";
import SheetDialog from "./SheetDialog";
import AddressForm, { alamatKosong, validasiAlamat, alamatSatuBaris, wilayahTerverifikasi } from "./AddressForm";

const HIJAU = "#1F6B3A";
const MERAH = "#A12622";
export const PESAN_BELUM_VERIFIKASI = "Wilayah belum terverifikasi. Pilih ulang kelurahan & kecamatan untuk memakai alamat ini.";

export const BadgeUtama = () => (
  <span className="shrink-0 rounded-[3px] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
    style={{ background: "#E8F5EC", color: HIJAU }}>Utama</span>
);

export const KartuAlamat = ({ a, children, pilihan }) => {
  const perlu = !wilayahTerverifikasi(a);
  return (
    <div className={`rounded-[8px] border p-3 ${perlu ? "border-[#E0B64A] bg-[#FDF6E3]" : "border-[#E5E5EA]"}`}
      data-testid={`alamat-kartu-${a.id}`}>
      <div className="flex items-start gap-3">
        {pilihan}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-bold text-[#1D1D1F]">{a.nama_alamat}</span>
            {a.utama && <BadgeUtama />}
          </div>
          <div className="mt-0.5 text-xs text-[#6E6E73]">{a.nama_penerima} · {formatTelepon(a.telepon)}</div>
          <div className="mt-1 text-sm text-[#424245]">{alamatSatuBaris(a)}</div>
          {a.patokan && <div className="mt-1 text-xs text-[#6E6E73]">Patokan: {a.patokan}</div>}
          {perlu && (
            <div className="mt-2 flex items-start gap-1.5 text-xs font-medium" style={{ color: "#7A5B10" }}
              data-testid={`alamat-belum-verifikasi-${a.id}`}>
              <AlertTriangle size={14} className="mt-[1px] shrink-0" />
              <span>{PESAN_BELUM_VERIFIKASI}</span>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default function AddressBookDialog({ onClose, onBack, onChanged }) {
  const [list, setList] = useState(null);
  const [mode, setMode] = useState("daftar");
  const [draft, setDraft] = useState(alamatKosong());
  const [editId, setEditId] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [hapusId, setHapusId] = useState(null);

  const muat = async () => {
    const { data } = await api.get("/api/addresses");
    setList(data);
    if (onChanged) onChanged(data);
  };
  useEffect(() => { muat().catch(() => setList([])); /* eslint-disable-next-line */ }, []);

  const bukaTambah = () => { setDraft({ ...alamatKosong(), utama: !(list || []).length }); setEditId(null); setErrors({}); setMode("form"); };
  const bukaUbah = (a) => { setDraft({ ...alamatKosong(), ...a }); setEditId(a.id); setErrors({}); setMode("form"); };

  const simpan = async () => {
    const e = validasiAlamat(draft);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      if (editId) await api.put(`/api/addresses/${editId}`, draft);
      else await api.post("/api/addresses", draft);
      await muat();
      toast.success(editId ? "Alamat diperbarui" : "Alamat ditambahkan");
      setMode("daftar");
    } catch (err) {
      toast.error(pesanGalatPembeli(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const jadikanUtama = async (a) => {
    try {
      const { data } = await api.post(`/api/addresses/${a.id}/utama`);
      setList(data);
      if (onChanged) onChanged(data);
      toast.success("Alamat utama diperbarui");
    } catch (err) {
      toast.error(pesanGalatPembeli(err.response?.data?.detail) || err.message);
    }
  };

  const hapus = async (a) => {
    try {
      const { data } = await api.delete(`/api/addresses/${a.id}`);
      setList(data);
      if (onChanged) onChanged(data);
      setHapusId(null);
      toast.success("Alamat dihapus");
    } catch (err) {
      setHapusId(null);
      toast.error(pesanGalatPembeli(err.response?.data?.detail) || err.message);
    }
  };

  if (mode === "form") {
    return (
      <SheetDialog title={editId ? "Ubah alamat" : "Tambah alamat"} onClose={onClose} onBack={() => setMode("daftar")} tid="alamat-form-dialog">
        <AddressForm value={draft} onChange={setDraft} errors={errors} tid="alamat" withUtama={!(editId && draft.utama)} />
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={simpan} disabled={busy} data-testid="alamat-simpan"
            className="rounded-[4px] bg-[#1D1D1F] px-5 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-60">
            {busy ? "Menyimpan…" : "Simpan alamat"}
          </button>
          <button type="button" onClick={() => setMode("daftar")} data-testid="alamat-form-batal"
            className="rounded-[4px] border border-[#E5E5EA] px-5 py-2.5 text-sm font-semibold text-[#424245] hover:bg-[#F5F5F7]">Batal</button>
        </div>
      </SheetDialog>
    );
  }

  return (
    <SheetDialog title="Alamat pengiriman" onClose={onClose} onBack={onBack} tid="alamat-dialog">
      {list === null ? (
        <p className="text-sm text-[#6E6E73]">Memuat alamat…</p>
      ) : (
        <div className="space-y-3" data-testid="alamat-list">
          {list.length === 0 && (
            <p className="text-sm text-[#6E6E73]" data-testid="alamat-kosong">Belum ada alamat tersimpan.</p>
          )}
          {list.map((a) => (
            <KartuAlamat key={a.id} a={a}>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-semibold">
                <button type="button" onClick={() => bukaUbah(a)} data-testid={`alamat-ubah-${a.id}`} className="text-[#1D1D1F] underline">Ubah</button>
                {!a.utama && (
                  <>
                    <button type="button" onClick={() => jadikanUtama(a)} data-testid={`alamat-utama-${a.id}`} className="text-[#1D1D1F] underline">Jadikan utama</button>
                    <button type="button" onClick={() => setHapusId(a.id)} data-testid={`alamat-hapus-${a.id}`} className="underline" style={{ color: MERAH }}>Hapus</button>
                  </>
                )}
              </div>
              {hapusId === a.id && (
                <div className="mt-2 rounded-[6px] border border-[#E5E5EA] bg-[#F5F5F7] p-3" data-testid={`alamat-hapus-konfirmasi-${a.id}`}>
                  <p className="text-xs text-[#1D1D1F]">Hapus alamat “{a.nama_alamat}”?</p>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => hapus(a)} data-testid={`alamat-hapus-ya-${a.id}`}
                      className="rounded-[4px] px-3 py-1.5 text-xs font-semibold text-white" style={{ background: MERAH }}>Ya, hapus</button>
                    <button type="button" onClick={() => setHapusId(null)} data-testid={`alamat-hapus-tidak-${a.id}`}
                      className="rounded-[4px] border border-[#E5E5EA] bg-white px-3 py-1.5 text-xs font-semibold text-[#424245]">Batal</button>
                  </div>
                </div>
              )}
            </KartuAlamat>
          ))}
          {list.length < 10 && (
            <button type="button" onClick={bukaTambah} data-testid="alamat-tambah"
              className="flex w-full items-center justify-center gap-2 rounded-[8px] border border-dashed border-[#6E6E73] px-3 py-3 text-sm font-semibold text-[#1D1D1F] hover:bg-[#F5F5F7]">
              <Plus size={16} strokeWidth={2} /> Tambah alamat
            </button>
          )}
          {list.length >= 10 && (
            <p className="text-xs text-[#6E6E73]" data-testid="alamat-maks">Maksimal 10 alamat per akun.</p>
          )}
        </div>
      )}
    </SheetDialog>
  );
}
